import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import {
  getInputElementGivenLabel,
  getSelectElementGivenLabel,
} from './linkedinHtmlElementUtils'
import { editProfileUrl } from './linkedinUrls'
import { LinkedinSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getDomPromise,
  getEventListenerPromise,
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import {
  setInputTextElement,
  setSelectElement,
} from '../reactInputElementUtils'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getElement } from '../utils'

interface LinkedinProfileLocation {
  countryOrRegion: string
  postalCode: string
  city: string
}

enum SectionLabel {
  COUNTRY_OR_REGION = 'Country/Region',
  POSTAL_CODE = 'Postal code',
  CITY = 'City',
}

// Helpers for settings properties

function parseProfileLocation(o: {
  [key: string]: any
}): LinkedinProfileLocation {
  return {
    countryOrRegion: o.countryOrRegion,
    postalCode: o.postalCode,
    city: o.city,
  }
}

function getCurrentValue(initialValues: { [key: string]: any }): string {
  const locationDataRaw =
    initialValues?.[LinkedinSettingKey.LOCATION_VISIBILITY]
  if (!locationDataRaw) {
    return 'None'
  }

  const locationData = parseProfileLocation(locationDataRaw)

  if (!locationData.countryOrRegion) {
    return 'None'
  }

  if (locationData.postalCode && locationData.city) {
    return `${locationData.city}, ${locationData.countryOrRegion} (${locationData.postalCode})`
  } else if (locationData.postalCode && !locationData.city) {
    return `${locationData.countryOrRegion} (${locationData.postalCode})`
  } else if (!locationData.postalCode && locationData.city) {
    return `${locationData.city}, ${locationData.countryOrRegion}`
  }
  return `${locationData.countryOrRegion}`
}

function getIsFulfilled(initialValues: { [key: string]: any }): boolean {
  const locationDataRaw =
    initialValues?.[LinkedinSettingKey.LOCATION_VISIBILITY]
  if (!locationDataRaw) {
    return false
  }

  const locationData = parseProfileLocation(locationDataRaw)

  return !locationData.postalCode && !locationData.city
}

const getCurrentValueFn = () => getCurrentValue
const getIsFulfilledFn = () => getIsFulfilled
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// DOM Helpers

function getLocationSection(): Promise<HTMLElement> {
  return getElement<HTMLElement>(
    document,
    'descendant-or-self::*[text() = "Location"]/parent::*',
  )
}

function waitForSectionElement(
  section: HTMLElement,
  label: SectionLabel,
): Promise<Node> {
  return getWaitForElementToAppearPromise(
    section,
    (node) =>
      evaluateXpath(
        node,
        `descendant-or-self::*[text() = "${label}" or contains(text(), "${label}")]`,
      )[0],
  )
}
function hasPostalCodeLabel(section: HTMLElement): boolean {
  return Boolean(
    evaluateXpath(
      section,
      `descendant-or-self::*[text() = "${SectionLabel.POSTAL_CODE}"]`,
    )[0],
  )
}

function getPostalCodeElementAndOnEventPromise(section: HTMLElement): {
  element: HTMLInputElement | null
  onEventPromise: Promise<void> | null
  setter: (value: string) => void
} {
  let element
  let onEventPromise
  if (hasPostalCodeLabel(section)) {
    element = getInputElementGivenLabel(section, SectionLabel.POSTAL_CODE)
    onEventPromise = getEventListenerPromise(element, 'input')
  } else {
    element = null
    onEventPromise = Promise.resolve(null)
  }
  return {
    element,
    onEventPromise,
    setter: element ? (value) => setInputTextElement(element, value) : () => {},
  }
}

function getCityElementAndOnEventPromise(section: HTMLElement): {
  element: HTMLInputElement | HTMLSelectElement | null
  onEventPromise: Promise<void> | null
  setter: (value: string) => Promise<void>
} {
  let element = null
  let hasCityInput = false
  let hasCitySelect = false

  // If fail to find input, try select
  try {
    element = getInputElementGivenLabel(section, SectionLabel.CITY)
    hasCityInput = true
  } catch {
    try {
      element = getSelectElementGivenLabel(section, SectionLabel.CITY)
      hasCitySelect = true
    } catch {
      // ignore; we handle this case in the if-else block below
    }
  }

  let onEventPromise
  let setter
  if (hasCityInput) {
    onEventPromise = getEventListenerPromise(element, 'input')
    setter = async (value) => {
      if (!value) {
        setInputTextElement(element, value)
        return
      }

      setInputTextElement(element, value)

      // unfocus (to exit out of the typeahead) & wait a little
      // else, will not validate & clicking save won't work
      const blurPromise = getEventListenerPromise(element, 'blur')
      element.dispatchEvent(new Event('blur', { bubbles: true }))
      await blurPromise
      await wait(2000)
    }
  } else if (hasCitySelect) {
    onEventPromise = getEventListenerPromise(element, 'change')
    setter = (value) => setSelectElement(element, value)
  } else {
    onEventPromise = Promise.resolve(null)
    setter = () => Promise.resolve()
  }
  return { element, onEventPromise, setter }
}

async function gatherProfileLocation(): Promise<LinkedinProfileLocation> {
  remoteLog.debug('gatherProfileLocation: Top')
  const locationSection = await getLocationSection()
  remoteLog.debug(
    `gatherProfileLocation: Got locationSection ${locationSection}`,
  )

  // Get country or region
  const countryOrRegionInputElement = getInputElementGivenLabel(
    locationSection,
    SectionLabel.COUNTRY_OR_REGION,
  )
  remoteLog.debug(
    `gatherProfileLocation: Got countryOrRegionInputElement ${countryOrRegionInputElement}`,
  )

  // May need to wait for data to load & populate input element
  if (!countryOrRegionInputElement.value) {
    remoteLog.debug(
      'gatherProfileLocation: Waiting for countryOrRegionInputElement.value',
    )
    await getDomPromise(document, () =>
      Boolean(countryOrRegionInputElement.value),
    )
  }

  remoteLog.debug(
    `gatherProfileLocation: Got countryOrRegionInputElement.value with length ${countryOrRegionInputElement.value.length}`,
  )

  const countryOrRegion = countryOrRegionInputElement.value

  if (!countryOrRegion) {
    const message =
      'gatherProfileLocation: Could not find country/region (a field required by LinkedIn)'
    remoteLog.error(message)
    throw new Error(message)
  }

  const { element: postalCodeInputElement } =
    getPostalCodeElementAndOnEventPromise(locationSection)
  remoteLog.debug(
    `gatherProfileLocation: Got postalCodeInputElement ${postalCodeInputElement}`,
  )
  const postalCode = postalCodeInputElement?.value
  remoteLog.debug(
    `gatherProfileLocation: Got postalCode with length ${postalCode?.length}`,
  )

  const { element: cityElement } =
    getCityElementAndOnEventPromise(locationSection)
  remoteLog.debug(`gatherProfileLocation: Got cityElement ${cityElement}`)
  const city = cityElement?.value
  remoteLog.debug(`gatherProfileLocation: Got city with length ${city?.length}`)

  remoteLog.debug(`gatherProfileLocation: Bottom, returning`)
  return {
    countryOrRegion,
    postalCode,
    city,
  }
}

async function clickSaveButton(): Promise<void> {
  const saveButton = evaluateXpath<HTMLElement>(
    document,
    'descendant-or-self::*[text() = "Save"]',
  )[0]

  if (!saveButton) {
    throw new Error('Could not find save button')
  }

  const anyDomChangePromise = getDomPromise(document, () => true)
  saveButton.click()
  await anyDomChangePromise
}

async function clickCloseIcon(): Promise<void> {
  const closeButton = evaluateXpath<HTMLElement>(
    document,
    'descendant-or-self::*[' +
      `  ${getCaseInsensitiveXPathTextSelector('aria-label', 'dismiss')}` +
      '  and count(./ancestor-or-self::*[@aria-hidden="true"]) = 0' +
      ']',
  )[0]

  if (!closeButton) {
    throw new Error('Could not find close button')
  }

  const anyDomChangePromise = getDomPromise(document, () => true)
  closeButton.click()
  await anyDomChangePromise
}

// Automations

enum StageKey {
  LINKEDIN_LOCATION_VISIBILITY_GATHER = 'LINKEDIN_LOCATION_VISIBILITY_GATHER',
  LINKEDIN_LOCATION_VISIBILITY_UPDATE = 'LINKEDIN_LOCATION_VISIBILITY_UPDATE',
  LINKEDIN_LOCATION_VISIBILITY_UNDO = 'LINKEDIN_LOCATION_VISIBILITY_UNDO',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_LOCATION_VISIBILITY_GATHER,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: editProfileUrl,
    action: async (machineContext, userContext) => {
      remoteLog.debug('LINKEDIN_LOCATION_VISIBILITY_GATHER action: Top')
      const { updateStoredInitialValues } = userContext

      remoteLog.debug(
        'LINKEDIN_LOCATION_VISIBILITY_GATHER action: Calling gatherProfileLocation',
      )
      const locationData = await gatherProfileLocation()

      remoteLog.debug(
        'LINKEDIN_LOCATION_VISIBILITY_GATHER action: Got locationData',
      )
      await updateStoredInitialValues(
        { [LinkedinSettingKey.LOCATION_VISIBILITY]: locationData },
        LinkedinRecommendationKey.LINKEDIN_LOCATION_VISIBILITY,
      )
      remoteLog.debug(
        'LINKEDIN_LOCATION_VISIBILITY_GATHER action: Updated stored initial values',
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_LOCATION_VISIBILITY_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: editProfileUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const initialLocationDataRaw =
        initialValues?.[LinkedinSettingKey.LOCATION_VISIBILITY]
      if (!initialLocationDataRaw) {
        throw new Error('No initial location data')
      }

      const initialLocationData = parseProfileLocation(initialLocationDataRaw)

      if (initialLocationData.countryOrRegion) {
        const locationSection = await getLocationSection()

        const {
          element: postalCodeInputElement,
          onEventPromise: postalCodeOnEventPromise,
          setter: postalCodeSetter,
        } = getPostalCodeElementAndOnEventPromise(locationSection)
        const { onEventPromise: cityOnEventPromise, setter: citySetter } =
          getCityElementAndOnEventPromise(locationSection)

        if (postalCodeInputElement) {
          postalCodeSetter('')
          await postalCodeOnEventPromise
        } else {
          await citySetter('')
          await cityOnEventPromise
        }

        if (isDry) {
          await clickCloseIcon()
        } else {
          await clickSaveButton()
        }
      }

      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_LOCATION_VISIBILITY_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: editProfileUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const initialLocationDataRaw =
        initialValues?.[LinkedinSettingKey.LOCATION_VISIBILITY]
      if (!initialLocationDataRaw) {
        throw new Error('No initial location data')
      }

      const initialLocationData = parseProfileLocation(initialLocationDataRaw)
      const currentLocationData = await gatherProfileLocation()

      const locationSection = await getLocationSection()

      let performedUpdates = false

      if (initialLocationData.postalCode || currentLocationData.postalCode) {
        await waitForSectionElement(locationSection, SectionLabel.POSTAL_CODE)

        const {
          onEventPromise: postalCodeOnEventPromise,
          setter: postalCodeSetter,
        } = getPostalCodeElementAndOnEventPromise(locationSection)

        postalCodeSetter(initialLocationData.postalCode)
        await postalCodeOnEventPromise

        performedUpdates = true
      }

      if (initialLocationData.city || currentLocationData.city) {
        await waitForSectionElement(locationSection, SectionLabel.CITY)
        const { onEventPromise: cityOnEventPromise, setter: citySetter } =
          getCityElementAndOnEventPromise(locationSection)

        await citySetter(initialLocationData.city)
        await cityOnEventPromise

        performedUpdates = true
      }

      if (isDry || !performedUpdates) {
        await clickCloseIcon()
      } else {
        await clickSaveButton()
      }

      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
